import type { Task, GroupingState, GroupByField, SortingState, SortByField, SortOrder, CustomField } from '@/types';
import { normalizeStatus, TASK_STATUS } from '@/constants/taskStatuses';

export interface GroupedItem<T> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  items: T[];
  subGroups?: GroupedItem<T>[];
}

export interface GroupLabel {
  key: string;
  label: string;
  icon?: React.ReactNode;
}

/**
* Gets the group key for a task based on the grouping field
*/
export function getTaskGroupKey(task: Task, groupBy: GroupByField, customFieldsMap?: Map<string, CustomField>): string {
  // Handle custom field grouping (format: 'customField:{fieldId}')
  if (typeof groupBy === 'string' && groupBy.startsWith('customField:')) {
    const fieldId = groupBy.replace('customField:', '');
    const field = customFieldsMap?.get(fieldId);
    const value = task.customFields?.[fieldId];

    if (!value || value === null || value === undefined || value === '') {
      return 'Senza valore';
    }

    // For dropdown fields, use the option label
    if (field && field.type === 'dropdown') {
      const option = field.options?.find(opt => opt.value === value);
      return option ? option.label : String(value);
    }

    // For date fields, format similar to dueDate
    if (field && field.type === 'date') {
      const date = value instanceof Date ? value : new Date(value as number);
      const today = new Date();
      const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return 'Scaduto';
      if (diffDays === 0) return 'Oggi';
      if (diffDays === 1) return 'Domani';
      if (diffDays <= 7) return 'Questa settimana';
      if (diffDays <= 30) return 'Questo mese';
      return 'Futuro';
    }

    // For text fields, use the value as-is
    return String(value);
  }

  switch (groupBy) {
    case 'status':
      return normalizeStatus(task.status);
    case 'assignee':
      return (task.assignees || [])[0] || 'Non assegnato';
    case 'priority':
      return task.priority || 'Nessuna priorità';
    case 'list':
      return task.listName || 'Senza lista';
    case 'folder':
      return (task.folderPath?.length ?? 0) > 0 ? task.folderPath!.join('/') : 'Senza cartella';
    case 'space':
      return task.spaceName || 'Senza spazio';
    case 'tags':
      return (task.tags || [])[0] || 'Senza tag';
    case 'dueDate': {
      if (!task.dueDate) return 'Senza scadenza';
      const today = new Date();
      const dueDate = typeof task.dueDate === 'number' ? new Date(task.dueDate) : task.dueDate;
      const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return 'Scaduto';
      if (diffDays === 0) return 'Oggi';
      if (diffDays === 1) return 'Domani';
      if (diffDays <= 7) return 'Questa settimana';
      if (diffDays <= 30) return 'Questo mese';
      return 'Futuro';
    }
    default:
      return 'default';
  }
}

/**
* Gets the display label for a group key
*/
export function getGroupLabel(key: string, groupBy: GroupByField, resolveDisplayName?: (id: string) => string): string {
  if (groupBy === 'status') {
    const statusLabels: Record<string, string> = {
      [TASK_STATUS.OPEN]: 'Da fare',
      [TASK_STATUS.TODO]: 'Da fare',
      [TASK_STATUS.IN_PROGRESS]: 'In corso',
      [TASK_STATUS.BLOCKED]: 'Bloccato',
      [TASK_STATUS.COMPLETED]: 'Completato',
      [TASK_STATUS.CANCELLED]: 'Annullato',
    };
    return statusLabels[key] || key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
  }

  if (groupBy === 'priority') {
    const priorityLabels: Record<string, string> = {
      'urgent': 'Urgente',
      'high': 'Alta',
      'normal': 'Normale',
      'low': 'Bassa',
      'Nessuna priorità': 'Nessuna priorità',
    };
    return priorityLabels[key] || key;
  }

  if (groupBy === 'assignee' && resolveDisplayName) {
    return resolveDisplayName(key);
  }

  return key;
}

/**
* Groups tasks by multiple levels (primary, secondary, tertiary)
*/
export function groupTasksByMultipleLevels(
  tasks: Task[],
  grouping: GroupingState,
  customFieldsMap?: Map<string, CustomField>
): GroupedItem<Task>[] {
  if (grouping.primary === 'none') {
    return [{
      key: 'all',
      label: 'Tutti i task',
      items: tasks
    }];
  }

  const groups = new Map<string, Task[]>();

  // First level grouping
  tasks.forEach(task => {
    const primaryKey = getTaskGroupKey(task, grouping.primary, customFieldsMap);
    const existing = groups.get(primaryKey) || [];
    existing.push(task);
    groups.set(primaryKey, existing);
  });

  // Convert to GroupedItem array and apply secondary/tertiary grouping
  const result: GroupedItem<Task>[] = Array.from(groups.entries())
    .map(([key, groupTasks]) => {
      const item: GroupedItem<Task> = {
        key,
        label: getGroupLabel(key, grouping.primary),
        items: groupTasks
      };

      // Apply secondary grouping if specified
      if (grouping.secondary !== 'none') {
        item.subGroups = groupTasksByLevel(groupTasks, grouping.secondary, customFieldsMap);
      }

      return item;
    })
    .sort((a, b) => {
      // Custom sorting for status groups
      if (grouping.primary === 'status') {
        const statusOrder: Record<string, number> = {
          [TASK_STATUS.IN_PROGRESS]: 1,
          [TASK_STATUS.OPEN]: 2,
          [TASK_STATUS.TODO]: 3,
          [TASK_STATUS.BLOCKED]: 4,
          [TASK_STATUS.COMPLETED]: 5,
          [TASK_STATUS.CANCELLED]: 6,
        };
        const orderA = statusOrder[a.key] ?? 99;
        const orderB = statusOrder[b.key] ?? 99;
        return orderA - orderB;
      }

      // Alphabetical sorting for other groups
      return a.label.localeCompare(b.label);
    });

  return result;
}

/**
* Helper function for single level grouping
*/
function groupTasksByLevel(tasks: Task[], groupBy: GroupByField, customFieldsMap?: Map<string, CustomField>): GroupedItem<Task>[] {
  if (groupBy === 'none') {
    return [{
      key: 'all',
      label: 'Tutti',
      items: tasks
    }];
  }

  const groups = new Map<string, Task[]>();

  tasks.forEach(task => {
    const key = getTaskGroupKey(task, groupBy, customFieldsMap);
    const existing = groups.get(key) || [];
    existing.push(task);
    groups.set(key, existing);
  });

  return Array.from(groups.entries())
    .map(([key, groupTasks]) => ({
      key,
      label: getGroupLabel(key, groupBy),
      items: groupTasks
    }))
    .sort((a, b) => {
      if (groupBy === 'status') {
        const statusOrder: Record<string, number> = {
          [TASK_STATUS.IN_PROGRESS]: 1,
          [TASK_STATUS.OPEN]: 2,
          [TASK_STATUS.TODO]: 3,
          [TASK_STATUS.BLOCKED]: 4,
          [TASK_STATUS.COMPLETED]: 5,
          [TASK_STATUS.CANCELLED]: 6,
        };
        const orderA = statusOrder[a.key] ?? 99;
        const orderB = statusOrder[b.key] ?? 99;
        return orderA - orderB;
      }
      return a.label.localeCompare(b.label);
    });
}

/**
* Flattens grouped items for virtualization, including headers
*/
export function flattenGroupedItems<T>(
  groups: GroupedItem<T>[],
  grouping: GroupingState,
  includeHeaders: boolean = true
): Array<{ type: 'header' | 'item' | 'group-footer'; data: unknown; key: string; level: number }> {
  // Ensure collapsedGroups is a Set (defensive check for deserialized state)
  const collapsedGroups = grouping.collapsedGroups instanceof Set
    ? grouping.collapsedGroups
    : new Set(grouping.collapsedGroups as string[]);
  const safeGrouping = { ...grouping, collapsedGroups };
  const items: Array<{ type: 'header' | 'item' | 'group-footer'; data: unknown; key: string; level: number }> = [];
  const usedKeys = new Set<string>();

  // IMPORTANT: keys must be stable across updates (e.g. editing tags) to avoid
  // React/virtualizer reusing the wrong row, which looks like tasks "shuffle".
  const getStableItemKey = (item: T, fallbackGroupKey: string, index: number) => {
    const maybeId = (item as unknown as { id?: unknown })?.id;
    if (typeof maybeId === 'string' && maybeId.length > 0) {
      // Include group key so the same item can exist in different groups without key collisions.
      // This prevents React warnings ("two children with the same key") which can manifest as
      // overlapped rows / incorrect row reuse in the virtualized list.
      const base = `item-${fallbackGroupKey}-${maybeId}`;
      if (!usedKeys.has(base)) {
        usedKeys.add(base);
        return base;
      }
      const withIndex = `${base}-${index}`;
      usedKeys.add(withIndex);
      return withIndex;
    }
    const base = `item-${fallbackGroupKey}-${index}`;
    if (!usedKeys.has(base)) {
      usedKeys.add(base);
      return base;
    }
    const withIndex = `${base}-dup${usedKeys.size}`;
    usedKeys.add(withIndex);
    return withIndex;
  };

  const processGroup = (group: GroupedItem<T>, level: number = 0) => {
    if (includeHeaders && level === 0) {
      items.push({
        type: 'header',
        data: group,
        key: `header-${group.key}`,
        level
      });
    }

    const hasSubGroups = !!group.subGroups && group.subGroups.length > 0;

    // If this group has subGroups, we only render leaf items via subGroups.
    // Rendering both group.items and subGroups would duplicate items (and with stable keys,
    // it causes React key collisions -> rows appear to overlap/shuffle).
    if (hasSubGroups) {
      group.subGroups!.forEach(subGroup => {
        processGroup(subGroup, level + 1);
      });
      return;
    }

    // Leaf group: add items (respect collapse only for top-level groups)
    if (level === 0 && safeGrouping.collapsedGroups.has(group.key)) {
      return;
    }

    group.items.forEach((item, index) => {
      items.push({
        type: 'item',
        data: item,
        key: getStableItemKey(item, group.key, index),
        level
      });
    });

    // Emit group footer after items (only for top-level groups with items)
    if (level === 0 && group.items.length > 0 && includeHeaders) {
      items.push({
        type: 'group-footer',
        data: group,
        key: `footer-${group.key}`,
        level
      });
    }
  };

  groups.forEach(group => processGroup(group));

  return items;
}

/**
* Gets available grouping options for tasks
*/
export function getTaskGroupingOptions(customFields?: CustomField[]): Array<{ value: GroupByField; label: string; icon?: React.ReactNode }> {
  const baseOptions: Array<{ value: GroupByField; label: string; icon?: React.ReactNode }> = [
    { value: 'none', label: 'Nessun raggruppamento' },
    { value: 'status', label: 'Stato' },
    { value: 'assignee', label: 'Assegnatario' },
    { value: 'priority', label: 'Priorità' },
    { value: 'list', label: 'Lista' },
    { value: 'folder', label: 'Cartella' },
    { value: 'space', label: 'Spazio' },
    { value: 'dueDate', label: 'Scadenza' },
    { value: 'tags', label: 'Tag' },
  ];

  // Add custom fields as grouping options
  if (customFields && customFields.length > 0) {
    const customFieldOptions = customFields.map(field => ({
      value: `customField:${field.id}` as GroupByField,
      label: field.name,
    }));
    return [...baseOptions, ...customFieldOptions];
  }

  return baseOptions;
}

/**
* Creates default grouping state
*/
export function createDefaultGroupingState(): GroupingState {
  return {
    primary: 'status',
    secondary: 'none',
    tertiary: 'none',
    collapsedGroups: new Set(),
  };
}

/**
* Creates default sorting state
*/
export function createDefaultSortingState(): SortingState {
  return {
    primary: {
      field: 'dueDate',
      order: 'asc'
    },
    secondary: {
      field: 'none',
      order: 'asc'
    }
  };
}

/**
* Gets available sorting options for tasks
*/
export function getTaskSortingOptions(customFields?: CustomField[]): Array<{ value: SortByField; label: string; icon?: React.ReactNode }> {
  const baseOptions: Array<{ value: SortByField; label: string; icon?: React.ReactNode }> = [
    { value: 'none', label: 'Nessuno' },
    { value: 'name', label: 'Nome' },
    { value: 'status', label: 'Stato' },
    { value: 'dueDate', label: 'Scadenza' },
    { value: 'dateCreated', label: 'Data creazione' },
    { value: 'priority', label: 'Priorità' },
    { value: 'assignee', label: 'Assegnatario' },
    { value: 'updated', label: 'Ultimo aggiornamento' },
  ];

  // Add custom fields as sorting options
  if (customFields && customFields.length > 0) {
    const customFieldOptions = customFields.map(field => ({
      value: `customField:${field.id}` as SortByField,
      label: field.name,
    }));
    return [...baseOptions, ...customFieldOptions];
  }

  return baseOptions;
}

/**
* Gets available sorting options for team members
*/
export function getTeamSortingOptions(): Array<{ value: SortByField; label: string; icon?: React.ReactNode }> {
  return [
    { value: 'none', label: 'Nessuno' },
    { value: 'name', label: 'Nome' },
    { value: 'tasks', label: 'Task totali' },
    { value: 'workload', label: 'Carico attivo' },
    { value: 'overdue', label: 'Task scaduti' },
    { value: 'completion', label: 'Completamento %' },
  ];
}

/**
* Sort function that handles multi-level sorting
*/
export function sortItems<T>(
  items: T[],
  sorting: SortingState,
  sortFn: (a: T, b: T, field: SortByField, order: SortOrder) => number
): T[] {
  if (sorting.primary.field === 'none') {
    return items;
  }

  return [...items].sort((a, b) => {
    // Primary sort
    let result = sortFn(a, b, sorting.primary.field, sorting.primary.order);

    // Detect unstable comparator results (e.g. Infinity - Infinity => NaN) which makes sort non-deterministic.
    if (Number.isNaN(result)) {
      result = 0;
    }

    // If equal and secondary sort is enabled, use secondary sort
    if (result === 0 && sorting.secondary.field !== 'none') {
      result = sortFn(a, b, sorting.secondary.field, sorting.secondary.order);
    }

    // Final deterministic tie-breaker to prevent reordering when input array order changes
    // (e.g. after updating a task's tags). Only applies when objects have string `id`.
    if (result === 0) {
      const ida = (a as unknown as { id?: unknown })?.id;
      const idb = (b as unknown as { id?: unknown })?.id;
      if (typeof ida === 'string' && typeof idb === 'string') {
        return ida.localeCompare(idb);
      }
    }

    return result;
  });
}

/**
* Toggles a group collapse state
*/
export function toggleGroupCollapse(
  grouping: GroupingState,
  groupKey: string
): GroupingState {
  const newCollapsedGroups = new Set(grouping.collapsedGroups);
  if (newCollapsedGroups.has(groupKey)) {
    newCollapsedGroups.delete(groupKey);
  } else {
    newCollapsedGroups.add(groupKey);
  }

  return {
    ...grouping,
    collapsedGroups: newCollapsedGroups,
  };
}


